// Multiplayer game logic for Wordsheet
// Wraps the local Game class and syncs with Firestore

import { db } from './firebase-config.js';
import { doc, getDoc, onSnapshot, runTransaction, arrayUnion, updateDoc } from 'firebase/firestore';
import { Game } from './game.js';
import { isValidWord } from './dictionary.js';
import { setLayoutByName } from './board.js';
import {
    MOVE_REASON,
    isPlayerActive,
    getTurnsTaken,
    canPlayerMove,
    findNextTurnIndex,
    getReasonMessage
} from '@wordsheet/shared';
import { flags } from './flags.js';
import { celebrate, startAmbientFireworks } from './celebration.js';

function getTurnTimeoutMs() {
    return flags.get('autoAwayMinutes') * 60 * 1000;
}

export class MultiplayerGame {
    constructor(gameCode, playerId, nickname) {
        this.gameCode = gameCode;
        this.playerId = playerId;
        this.nickname = nickname;

        this.gameRef = doc(db, 'games', gameCode);
        this.unsubscribe = null;

        this.localGame = null;
        this.gameState = null;
        this.isMyTurn = false;
        this.wasMyTurn = false; // Track previous state for notifications
        this.isSubmitting = false; // Prevent double submissions
        this.isAwaitingTurnUpdate = false; // Keep UI locked until server confirms move
        this.isCheckingTimeout = false; // Prevent concurrent timeout checks
        this.isCheckingAllIdle = false; // Prevent concurrent all-idle checks
        this.lastSubmittedMoveId = null;
        this.lastTurnIndexAtSubmit = null;
        this.lastBoardSignature = null;
        this.lastRackSignature = null;
        this.audioContext = null; // For notification sounds
        this.originalTitle = document.title; // Store original tab title
        this.timeoutCheckInterval = null; // Periodic timeout checker
        this.softGameOverActive = false;
        this.lastLocalActivity = Date.now(); // Track local activity to prevent false auto-away
        this.cleanupAmbientFireworks = null;

        // UI elements
        this.playerListEl = document.getElementById('game-players');
        this.moveHistoryEl = document.getElementById('move-history');
        this.messageEl = document.getElementById('message');
        this.controlsEl = document.querySelector('.controls');
    }

    async init() {
        // Get initial game state
        const docSnap = await getDoc(this.gameRef);
        if (!docSnap.exists()) {
            throw new Error('Game not found');
        }

        this.gameState = docSnap.data();

        // Create local game instance (without auto-drawing tiles)
        this.localGame = new Game({ isMultiplayer: true, skipTileBag: true });

        // Override the local game's submit method
        this.localGame.submitWord = () => this.submitMove();

        // Override the local game's swap method
        this.localGame.executeSwap = () => this.submitSwap();

        // Override enterSwapMode to check turn
        const originalEnterSwapMode = this.localGame.enterSwapMode.bind(this.localGame);
        this.localGame.enterSwapMode = () => {
            if (!this.isMyTurn) {
                this.showMessage("It's not your turn!", 'error');
                return;
            }
            if (this.gameState.tileBag.length === 0) {
                this.showMessage('No tiles left in the bag to swap with!', 'error');
                return;
            }
            originalEnterSwapMode();
        };

        // Override skipTurn to toggle away mode
        this.localGame.skipTurn = () => {
            if (this.isCurrentPlayerIdle()) {
                this.comeBackFromAway();
            } else {
                this.snooze();
            }
        };

        // Override finishGame for multiplayer
        this.localGame.finishGame = () => this.finishGame();

        // Override tile bag counter and endgame buttons for multiplayer
        this.localGame.updateTileBagCounter = () => {
            // Do nothing - handled by multiplayer
        };
        this.localGame.updateEndgameButtons = () => {
            // Do nothing - handled by multiplayer
        };

        // Request notification permission for turn alerts
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }

        // Load the board state
        this.loadBoardState();

        // Load our rack from server state
        this.loadRackState();

        // Update turn state
        this.updateTurnState();

        // Render players and history
        this.renderPlayers();
        this.renderMoveHistory();
        this.updateTileBagCounter();

        // Subscribe to game updates
        this.subscribeToGame();

        // Start periodic timeout check (every 10 seconds)
        this.timeoutCheckInterval = setInterval(() => {
            this.checkTurnTimeout();
        }, 10000);

        // Listen for any user activity to return from away mode
        this.setupActivityListeners();

        // Apply initial game flags if present
        if (this.gameState.gameFlags) {
            flags.applyGameFlags(this.gameState.gameFlags);
        }

        // Sync flag changes to Firestore when admin panel is used
        flags.onFlagChanged = (flag, value) => {
            if (!this.gameRef || this.gameState?.status !== 'active') return;
            if (flag === '__reset') {
                // Reset sends all default flags
                updateDoc(this.gameRef, { gameFlags: value }).catch(e =>
                    console.warn('Failed to sync flags:', e)
                );
            } else {
                updateDoc(this.gameRef, { [`gameFlags.${flag}`]: value }).catch(e =>
                    console.warn('Failed to sync flag:', e)
                );
            }
        };
    }

    setupActivityListeners() {
        const gameContainer = document.querySelector('.game-container') || document.body;

        const onActivity = () => {
            // Track local activity to prevent false auto-away timeouts
            // This ensures any interaction (tile picker, board clicks, etc.) resets the timer
            this.lastLocalActivity = Date.now();

            // Check if current player is idle and bring them back
            if (this.isCurrentPlayerIdle()) {
                this.comeBackFromAway();
            }
        };

        // Listen for clicks and key presses anywhere in the game
        gameContainer.addEventListener('click', onActivity);
        gameContainer.addEventListener('keydown', onActivity);

        // Store references for cleanup
        this.activityHandler = onActivity;
        this.activityContainer = gameContainer;
    }

    isCurrentPlayerIdle() {
        if (!this.gameState || !this.gameState.players) return false;
        const player = this.gameState.players.find(p => p.id === this.playerId);
        return player?.isIdle === true;
    }

    subscribeToGame() {
        this.unsubscribe = onSnapshot(this.gameRef, (docSnap) => {
            if (!docSnap.exists()) {
                console.error('Game was deleted');
                return;
            }

            const newState = docSnap.data();
            const stateChanged = JSON.stringify(this.gameState) !== JSON.stringify(newState);

            if (stateChanged) {
                this.gameState = newState;
                this.onGameStateUpdate();
            }
        }, (error) => {
            console.error('Error listening to game:', error);
        });
    }

    onGameStateUpdate() {
        // Apply synced game flags before anything reads them
        if (this.gameState.gameFlags) {
            flags.applyGameFlags(this.gameState.gameFlags);
        }

        const boardSignature = this.getBoardSignature(this.gameState.boardTiles || []);
        if (boardSignature !== this.lastBoardSignature) {
            this.loadBoardState();
        } else {
            this.localGame.isFirstMove = this.gameState.isFirstMove;
        }

        // Only reload rack if player doesn't have pending tiles on the board
        // (to avoid overwriting their in-progress move)
        const pendingTiles = this.localGame.board.getPendingTiles();
        if (pendingTiles.length === 0) {
            this.loadRackState();
        }

        // Clear submit lock once we see the move applied or turn advances
        if (this.isAwaitingTurnUpdate) {
            const moves = this.gameState.moves || [];
            const moveApplied = this.lastSubmittedMoveId && moves.some(m => m.moveId === this.lastSubmittedMoveId);
            const turnAdvanced = typeof this.lastTurnIndexAtSubmit === 'number' &&
                this.gameState.currentTurnIndex !== this.lastTurnIndexAtSubmit;

            if (moveApplied || turnAdvanced) {
                this.isAwaitingTurnUpdate = false;
                this.lastSubmittedMoveId = null;
                this.lastTurnIndexAtSubmit = null;
            }
        }

        // Update turn state
        this.updateTurnState();

        // Render players, history, and tile count
        this.renderPlayers();
        this.renderMoveHistory();
        this.updateTileBagCounter();

        // Check for game end or soft game over
        if (this.gameState.status === 'ended') {
            if (this.softGameOverActive) {
                this.upgradeSoftToTrueGameOver();
            } else {
                this.showGameOver();
            }
            return;
        } else if (this.softGameOverActive) {
            this.refreshSoftScoreboard();
        } else if (this.shouldShowSoftGameOver()) {
            this.showSoftGameOver();
        }

        // Check if current player has timed out
        this.checkTurnTimeout();

        // Check if all remaining players are idle and auto-end if needed
        this.checkAllIdleAndAutoEnd();
    }

    async checkAllIdleAndAutoEnd() {
        if (this.isCheckingAllIdle || this.gameState.status === 'ended') {
            return;
        }

        // Check if all remaining active players are idle
        const activePlayers = this.gameState.players.filter(p => isPlayerActive(p));
        if (activePlayers.length === 0) {
            return; // Game should already be ended
        }

        const allAreIdle = activePlayers.every(p => p.isIdle);
        if (!allAreIdle || !flags.get('autoEndWhenAllIdle')) {
            return;
        }

        // All remaining active players are idle - auto-finish them
        this.isCheckingAllIdle = true;

        try {
            await runTransaction(db, async (transaction) => {
                const docSnap = await transaction.get(this.gameRef);
                if (!docSnap.exists()) return;

                const data = docSnap.data();
                if (data.status === 'ended') return;

                // Re-check inside transaction
                const currentActivePlayers = data.players.filter(p => isPlayerActive(p));
                if (currentActivePlayers.length === 0) return;
                if (!currentActivePlayers.every(p => p.isIdle)) return;

                // Auto-finish all idle players
                const updatedPlayers = data.players.map(p => {
                    if (isPlayerActive(p) && p.isIdle) {
                        return {
                            ...p,
                            isActive: false,
                            isFinished: true
                        };
                    }
                    return p;
                });

                // Create auto-finish move records for each idle player
                const moveRecords = currentActivePlayers.map(p => ({
                    playerId: p.id,
                    nickname: p.nickname,
                    timestamp: Date.now(),
                    type: 'auto-finish',
                    words: [],
                    score: 0
                }));

                transaction.update(this.gameRef, {
                    players: updatedPlayers,
                    status: 'ended',
                    moves: arrayUnion(...moveRecords)
                });
            });
        } catch (error) {
            console.error('Failed to auto-end game:', error);
        } finally {
            this.isCheckingAllIdle = false;
        }
    }

    async checkTurnTimeout() {
        if (this.isCheckingTimeout || this.gameState.status === 'ended') {
            return;
        }

        const currentPlayer = this.gameState.players[this.gameState.currentTurnIndex];
        if (!currentPlayer || !isPlayerActive(currentPlayer) || currentPlayer.isIdle) {
            return;
        }

        // For the local player, use the most recent of server lastActiveAt and local activity
        // This prevents false auto-away when player is actively interacting (tile picker, etc.)
        let lastActive = Math.max(currentPlayer.lastActiveAt || 0, this.gameState.startedAt || 0);
        if (currentPlayer.id === this.playerId && this.lastLocalActivity) {
            lastActive = Math.max(lastActive, this.lastLocalActivity);
        }
        const elapsed = Date.now() - lastActive;

        if (elapsed < getTurnTimeoutMs()) {
            return;
        }

        // Current player has timed out - skip their turn
        this.isCheckingTimeout = true;

        try {
            await runTransaction(db, async (transaction) => {
                const docSnap = await transaction.get(this.gameRef);
                if (!docSnap.exists()) return;

                const data = docSnap.data();
                const currentIdx = data.currentTurnIndex;
                const player = data.players[currentIdx];

                // Re-check timeout inside transaction
                if (!player || !isPlayerActive(player)) return;
                const playerLastActive = player.lastActiveAt || 0;
                if (Date.now() - playerLastActive < getTurnTimeoutMs()) return;

                // Mark player as idle (and optionally finish them if flag is set)
                const updatedPlayers = [...data.players];
                const shouldAutoFinish = flags.get('autoEndWhenAllIdle');
                updatedPlayers[currentIdx] = {
                    ...updatedPlayers[currentIdx],
                    isIdle: true,
                    isActive: shouldAutoFinish ? false : updatedPlayers[currentIdx].isActive,
                    isFinished: shouldAutoFinish ? true : updatedPlayers[currentIdx].isFinished
                };

                // Find next active player
                const { nextIndex: nextTurnIndex, isIdlePlayer } = findNextTurnIndex(
                    updatedPlayers, currentIdx, null
                );

                // Give the next player a fresh timeout window (if there is one)
                if (nextTurnIndex !== currentIdx && isPlayerActive(updatedPlayers[nextTurnIndex])) {
                    updatedPlayers[nextTurnIndex] = {
                        ...updatedPlayers[nextTurnIndex],
                        lastActiveAt: Date.now()
                    };
                }

                // Check if game should end (all players finished)
                const activePlayers = updatedPlayers.filter(p => isPlayerActive(p));
                const newStatus = activePlayers.length === 0 ? 'ended' : data.status;

                const moveRecord = {
                    playerId: player.id,
                    nickname: player.nickname,
                    timestamp: Date.now(),
                    type: 'timeout',
                    words: [],
                    score: 0
                };

                transaction.update(this.gameRef, {
                    players: updatedPlayers,
                    currentTurnIndex: nextTurnIndex,
                    status: newStatus,
                    moves: arrayUnion(moveRecord)
                });
            });
        } catch (error) {
            console.error('Failed to skip timed-out player:', error);
        } finally {
            this.isCheckingTimeout = false;
        }
    }

    loadBoardState() {
        // Set the board layout from game state (must happen before initBoard)
        // Fallback to "Ring Pattern" for games created before layoutName was added
        const layoutName = this.gameState.layoutName || 'Ring Pattern';
        setLayoutByName(layoutName);

        // Save pending tiles before the snapshot wipes them
        const savedPending = this.localGame.board.getPendingTiles();

        const tiles = this.gameState.boardTiles || [];
        this.localGame.board.loadSnapshot(tiles);
        this.localGame.isFirstMove = this.gameState.isFirstMove;
        this.lastBoardSignature = this.getBoardSignature(tiles);

        // Restore pending tiles that don't conflict with new locked tiles
        for (const { row, col, tile } of savedPending) {
            if (this.localGame.board.cells[row][col].locked) {
                // Conflict: another player placed a tile here — return to rack
                if (tile.isBlank) { tile.assignedLetter = null; }
                this.localGame.rack.push(tile);
            } else {
                this.localGame.board.placeTile(row, col, tile, true);
            }
        }

        if (savedPending.length > 0) {
            this.localGame.renderRack();
        }
    }

    getBoardSignature(tiles) {
        if (!tiles || tiles.length === 0) {
            return '';
        }
        return tiles
            .map(tile => `${tile.row},${tile.col},${tile.id},${tile.assignedLetter || ''}`)
            .sort()
            .join('|');
    }

    getRackSignature(rack) {
        if (!rack || rack.length === 0) return '';
        // Sort by ID to detect tile changes regardless of order
        return rack
            .map(t => t.id)
            .sort()
            .join(',');
    }

    loadRackState() {
        const myPlayer = this.gameState.players.find(p => p.id === this.playerId);
        if (myPlayer && myPlayer.rack) {
            const newRackSignature = this.getRackSignature(myPlayer.rack);
            // Only reload rack if the tiles themselves have changed
            // (not just because of an update with same tiles in different order)
            if (newRackSignature !== this.lastRackSignature) {
                // Convert rack data to tile objects
                this.localGame.rack = myPlayer.rack.map(t => ({
                    letter: t.letter,
                    points: t.points,
                    isBlank: t.isBlank,
                    id: t.id,
                    assignedLetter: t.assignedLetter || null
                }));
                this.localGame.renderRack();
                this.lastRackSignature = newRackSignature;
            }
        }

        // Update local score display
        if (myPlayer) {
            this.localGame.score = myPlayer.score;
            document.getElementById('score').textContent = myPlayer.score;
        }
    }

    // Note: isPlayerActive, getTurnsTaken, canPlayerMove, findNextTurnIndex
    // are imported from @wordsheet/shared

    // Play a notification sound using Web Audio API
    playNotificationSound() {
        try {
            if (!this.audioContext) {
                this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            }
            const ctx = this.audioContext;

            // Create a pleasant two-tone chime
            const playTone = (freq, startTime, duration) => {
                const oscillator = ctx.createOscillator();
                const gainNode = ctx.createGain();

                oscillator.connect(gainNode);
                gainNode.connect(ctx.destination);

                oscillator.frequency.value = freq;
                oscillator.type = 'sine';

                gainNode.gain.setValueAtTime(0.3, startTime);
                gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

                oscillator.start(startTime);
                oscillator.stop(startTime + duration);
            };

            const now = ctx.currentTime;
            playTone(587.33, now, 0.15);        // D5
            playTone(880, now + 0.15, 0.2);     // A5
        } catch (e) {
            console.log('Could not play notification sound:', e);
        }
    }

    // Show a browser notification
    async showBrowserNotification() {
        if (!('Notification' in window)) {
            return;
        }

        // Request permission if needed
        if (Notification.permission === 'default') {
            await Notification.requestPermission();
        }

        if (Notification.permission === 'granted') {
            const notification = new Notification('Wordsheet', {
                body: "It's your turn!",
                icon: '/favicon.ico',
                tag: 'turn-notification', // Prevents duplicate notifications
                requireInteraction: false
            });

            // Close after 5 seconds
            setTimeout(() => notification.close(), 5000);

            // Focus window when clicked
            notification.onclick = () => {
                window.focus();
                notification.close();
            };
        }
    }

    // Notify user when it becomes their turn
    notifyTurn() {
        // Always play sound when it's your turn
        this.playNotificationSound();

        // Only show browser notification if page is hidden
        if (document.hidden) {
            this.showBrowserNotification();
        }
    }

    // Update tab title to indicate it's your turn
    updateTabTitle(isYourTurn) {
        if (isYourTurn) {
            document.title = '🟢 Your Turn - Wordsheet';
        } else {
            document.title = this.originalTitle;
        }
    }

    updateTurnState() {
        if (this.isSubmitting) {
            this.controlsEl.classList.add('disabled');
            this.showMessage('Submitting move...', 'info');
            return;
        }

        if (this.isAwaitingTurnUpdate) {
            this.controlsEl.classList.add('disabled');
            return;
        }

        // Use shared canPlayerMove function
        const moveStatus = canPlayerMove(
            this.gameState.players,
            this.gameState.currentTurnIndex,
            this.playerId
        );

        const wasMyTurn = this.isMyTurn;
        this.isMyTurn = moveStatus.canMove;

        // Update tab title based on turn (only for regular turns, not catch-up/returning)
        this.updateTabTitle(moveStatus.canMove && moveStatus.reason === MOVE_REASON.TURN);

        // Notify if it just became our turn (and it's a regular turn)
        if (moveStatus.canMove && !wasMyTurn && moveStatus.reason === MOVE_REASON.TURN) {
            this.notifyTurn();
        }

        // Re-enable controls after submission completes (controls stay active for tile arrangement)
        this.controlsEl.classList.remove('disabled');

        // Show appropriate message based on reason
        // But don't overwrite swap mode instructions
        if (this.localGame && this.localGame.swapMode) {
            return;
        }

        const message = getReasonMessage(moveStatus.reason);
        if (moveStatus.canMove) {
            this.showMessage(message || "It's your turn!", 'success');
        } else {
            // For waiting, show who we're waiting for
            const currentPlayer = this.gameState.players[this.gameState.currentTurnIndex];
            if (currentPlayer && isPlayerActive(currentPlayer)) {
                this.showMessage(`Waiting for ${currentPlayer.nickname}...`, 'info');
            } else {
                this.showMessage(message || 'Waiting for players...', 'info');
            }
        }
    }

    renderPlayers() {
        this.playerListEl.innerHTML = '';

        this.gameState.players.forEach((player, index) => {
            const li = document.createElement('li');

            if (index === this.gameState.currentTurnIndex) {
                li.classList.add('current-turn');
            }
            if (player.id === this.playerId) {
                li.classList.add('you');
            }
            if (!isPlayerActive(player)) {
                li.classList.add('inactive');
            }
            if (player.isIdle) {
                li.classList.add('idle');
            }

            const nameSpan = document.createElement('span');
            nameSpan.className = 'player-name';
            nameSpan.textContent = player.nickname;

            const scoreSpan = document.createElement('span');
            scoreSpan.className = 'player-score';
            scoreSpan.textContent = player.score;

            const turnsSpan = document.createElement('span');
            turnsSpan.className = 'player-turns';
            turnsSpan.textContent = `(${getTurnsTaken(player)})`;

            li.appendChild(nameSpan);
            li.appendChild(scoreSpan);
            li.appendChild(turnsSpan);
            this.playerListEl.appendChild(li);
        });
    }

    renderMoveHistory() {
        this.moveHistoryEl.innerHTML = '';

        // Show most recent moves first (reversed)
        const moves = [...(this.gameState.moves || [])].reverse();

        for (const move of moves) {
            const li = document.createElement('li');

            if (move.type === 'pass' || move.type === 'snooze') {
                const playerSpan = document.createElement('span');
                playerSpan.className = 'move-player';
                playerSpan.textContent = move.nickname;

                const passSpan = document.createElement('span');
                passSpan.className = 'move-pass';
                passSpan.textContent = move.type === 'snooze' ? 'went away' : 'passed';

                li.appendChild(playerSpan);
                li.appendChild(document.createTextNode(' '));
                li.appendChild(passSpan);
            } else if (move.type === 'swap') {
                const playerSpan = document.createElement('span');
                playerSpan.className = 'move-player';
                playerSpan.textContent = move.nickname;

                const swapSpan = document.createElement('span');
                swapSpan.className = 'move-pass';
                swapSpan.textContent = `swapped ${move.swapCount} tile${move.swapCount > 1 ? 's' : ''}`;

                li.appendChild(playerSpan);
                li.appendChild(document.createTextNode(' '));
                li.appendChild(swapSpan);
            } else if (move.type === 'timeout') {
                const playerSpan = document.createElement('span');
                playerSpan.className = 'move-player';
                playerSpan.textContent = move.nickname;

                const timeoutSpan = document.createElement('span');
                timeoutSpan.className = 'move-pass';
                timeoutSpan.textContent = 'went auto-away';

                li.appendChild(playerSpan);
                li.appendChild(document.createTextNode(' '));
                li.appendChild(timeoutSpan);
            } else if (move.type === 'return') {
                const playerSpan = document.createElement('span');
                playerSpan.className = 'move-player';
                playerSpan.textContent = move.nickname;

                const returnSpan = document.createElement('span');
                returnSpan.className = 'move-return';
                returnSpan.textContent = 'came back';

                li.appendChild(playerSpan);
                li.appendChild(document.createTextNode(' '));
                li.appendChild(returnSpan);
            } else if (move.type === 'finish') {
                const playerSpan = document.createElement('span');
                playerSpan.className = 'move-player';
                playerSpan.textContent = move.nickname;

                const finishSpan = document.createElement('span');
                finishSpan.className = 'move-pass';
                finishSpan.textContent = 'finished';

                li.appendChild(playerSpan);
                li.appendChild(document.createTextNode(' '));
                li.appendChild(finishSpan);
            } else if (move.type === 'bonus') {
                const playerSpan = document.createElement('span');
                playerSpan.className = 'move-player';
                playerSpan.textContent = move.nickname;

                const bonusSpan = document.createElement('span');
                bonusSpan.className = 'move-score';
                bonusSpan.textContent = `joined +${move.score}`;

                li.appendChild(document.createTextNode('\uD83C\uDFA1 '));
                li.appendChild(playerSpan);
                li.appendChild(document.createTextNode(' '));
                li.appendChild(bonusSpan);
            } else {
                const playerSpan = document.createElement('span');
                playerSpan.className = 'move-player';
                playerSpan.textContent = move.nickname;

                const wordsSpan = document.createElement('span');
                wordsSpan.className = 'move-words';
                wordsSpan.textContent = (move.words || []).join(', ');

                const scoreSpan = document.createElement('span');
                scoreSpan.className = 'move-score';
                scoreSpan.textContent = `+${move.score}`;

                li.appendChild(playerSpan);
                li.appendChild(document.createTextNode(': '));
                li.appendChild(wordsSpan);
                li.appendChild(document.createTextNode(' '));
                li.appendChild(scoreSpan);

                // Show "finished" indicator for auto-finish moves
                if (move.autoFinish) {
                    const finishSpan = document.createElement('span');
                    finishSpan.className = 'move-pass';
                    finishSpan.textContent = ' (finished)';
                    li.appendChild(finishSpan);
                }
            }

            this.moveHistoryEl.appendChild(li);
        }
    }

    updateTileBagCounter() {
        const tilesRemainingEl = document.getElementById('tiles-remaining');
        if (tilesRemainingEl && this.gameState.tileBag) {
            tilesRemainingEl.textContent = this.gameState.tileBag.length;
        }
        const bagNameEl = document.getElementById('tile-bag-name');
        if (bagNameEl && this.gameState.tileBagName) {
            bagNameEl.textContent = this.gameState.tileBagName;
        }
        this.updateEndgameButtons();
    }

    updateEndgameButtons() {
        const bagEmpty = this.gameState.tileBag && this.gameState.tileBag.length === 0;
        const controlsStack = document.querySelector('.controls-stack');
        const finishButton = document.getElementById('finish-game');

        if (bagEmpty) {
            if (controlsStack) controlsStack.classList.add('hidden');
            if (finishButton) finishButton.classList.remove('hidden');
        } else {
            if (controlsStack) controlsStack.classList.remove('hidden');
            if (finishButton) finishButton.classList.add('hidden');
        }
    }

    async submitMove() {
        if (!this.isMyTurn) {
            this.showMessage("It's not your turn!", 'error');
            return;
        }

        if (this.isSubmitting) {
            return; // Prevent double submission
        }

        const pending = this.localGame.board.getPendingTiles();

        if (pending.length === 0) {
            this.showMessage('Place some tiles on the board first!', 'error');
            return;
        }

        // Check for unassigned blank tiles
        for (const { row, col } of pending) {
            const tile = this.localGame.board.getTileAt(row, col);
            if (tile && tile.isBlank && !tile.assignedLetter) {
                this.showMessage('Assign a letter to the blank tile first!', 'error');
                return;
            }
        }

        // Validate placement
        const validation = this.localGame.validatePlacement(pending);
        if (!validation.valid) {
            this.showMessage(validation.message, 'error');
            return;
        }

        // Check first move covers a center cell
        if (this.gameState.isFirstMove) {
            const coversCenter = pending.some(p => this.localGame.board.isCenterCell(p.row, p.col));
            if (!coversCenter) {
                this.showMessage('First word must cover the center star!', 'error');
                return;
            }
        }

        // Check connection
        if (!this.gameState.isFirstMove) {
            const isConnected = this.localGame.checkConnection(pending);
            if (!isConnected) {
                this.showMessage('New tiles must connect to existing words!', 'error');
                return;
            }
        }

        // Find all words
        const words = this.localGame.findAllWords(pending, validation.direction);
        if (words.length === 0) {
            this.showMessage('Words must be at least 2 letters.', 'error');
            return;
        }

        // Validate words
        const invalidWords = words.filter(w => !isValidWord(w.word));
        if (invalidWords.length > 0) {
            const wordList = invalidWords.map(w => w.word).join(', ');
            this.showMessage(`Invalid word(s): ${wordList}`, 'error');
            return;
        }

        // Calculate score
        const totalScore = this.localGame.calculateScore(words);

        // Submit to Firestore
        const moveId = this.createMoveId();
        this.isSubmitting = true;
        this.controlsEl.classList.add('disabled');
        this.showMessage('Submitting move...', 'info');
        this.lastSubmittedMoveId = moveId;
        this.lastTurnIndexAtSubmit = this.gameState.currentTurnIndex;
        try {
            await this.submitToFirestore(pending, words, totalScore, moveId);
            this.isAwaitingTurnUpdate = true;
            this.controlsEl.classList.add('disabled');

            const wordList = words.map(w => w.word).join(', ');
            this.showMessage(`+${totalScore} points! Words: ${wordList}`, 'success');
        } catch (error) {
            console.error('Failed to submit move:', error);
            this.lastSubmittedMoveId = null;
            this.lastTurnIndexAtSubmit = null;
            this.isAwaitingTurnUpdate = false;
            if (error && error.message === 'NOT_YOUR_TURN') {
                this.showMessage('Turn already advanced. Please wait for your turn.', 'error');
            } else if (error && error.message === 'DUPLICATE_MOVE') {
                this.showMessage('Move already submitted. Please wait for sync.', 'error');
            } else if (error && (error.message === 'TILES_NOT_IN_RACK' || error.message === 'BOARD_CHANGED')) {
                this.showMessage('Move already applied or board updated. Please wait for sync.', 'error');
            } else {
                this.showMessage('Failed to submit move. Please try again.', 'error');
            }
        } finally {
            this.isSubmitting = false;
        }
    }

    async submitToFirestore(pending, words, score, moveId) {
        // Use a transaction to ensure atomic update
        await runTransaction(db, async (transaction) => {
            const docSnap = await transaction.get(this.gameRef);
            if (!docSnap.exists()) {
                throw new Error('Game not found');
            }

            const data = docSnap.data();
            const playerIndex = data.players.findIndex(p => p.id === this.playerId);

            if (playerIndex === -1) {
                throw new Error('Player not in game');
            }

            // Check if player can move using shared logic
            const moveStatus = canPlayerMove(data.players, data.currentTurnIndex, this.playerId);
            if (!moveStatus.canMove) {
                throw new Error('NOT_YOUR_TURN');
            }

            const isCurrentTurn = moveStatus.reason === MOVE_REASON.TURN;
            const isCatchUpMove = moveStatus.reason === MOVE_REASON.BEHIND;

            const boardTiles = data.boardTiles || [];
            const existingMoves = data.moves || [];
            if (moveId && existingMoves.some(m => m.moveId === moveId)) {
                throw new Error('DUPLICATE_MOVE');
            }
            const occupiedCells = new Set(boardTiles.map(t => `${t.row},${t.col}`));
            const boardTileIds = new Set(boardTiles.map(t => t.id));
            const player = data.players[playerIndex];
            const rackIds = new Set((player.rack || []).map(t => t.id));
            for (const { row, col, tile } of pending) {
                if (!rackIds.has(tile.id)) {
                    throw new Error('TILES_NOT_IN_RACK');
                }
                if (occupiedCells.has(`${row},${col}`) || boardTileIds.has(tile.id)) {
                    throw new Error('BOARD_CHANGED');
                }
            }

            // Get current board tiles and add new ones
            const newBoardTiles = [...boardTiles];
            for (const { row, col, tile } of pending) {
                newBoardTiles.push({
                    row,
                    col,
                    letter: tile.letter,
                    points: tile.points,
                    isBlank: tile.isBlank,
                    assignedLetter: tile.assignedLetter || null,
                    id: tile.id
                });
            }

            // Draw new tiles from bag
            const tileBag = [...data.tileBag];
            const newRack = this.localGame.rack.filter(t =>
                !pending.some(p => p.tile.id === t.id)
            );

            // Draw tiles to fill rack back to 7
            const tilesToDraw = Math.min(7 - newRack.length, tileBag.length);
            for (let i = 0; i < tilesToDraw; i++) {
                newRack.push(tileBag.pop());
            }

            // Check for automatic game end (used all tiles with empty bag)
            const autoFinish = newRack.length === 0 && tileBag.length === 0;

            // Check if player was idle (for return message)
            const wasIdle = data.players[playerIndex].isIdle;

            // Update player data
            const updatedPlayers = [...data.players];
            const currentTurns = getTurnsTaken(updatedPlayers[playerIndex]);
            updatedPlayers[playerIndex] = {
                ...updatedPlayers[playerIndex],
                score: updatedPlayers[playerIndex].score + score,
                wordsPlayed: (updatedPlayers[playerIndex].wordsPlayed || 0) + 1,
                turnsTaken: currentTurns + 1,
                rack: newRack,
                lastActiveAt: Date.now(),
                isIdle: false,  // Clear idle status when player acts
                isActive: autoFinish ? false : (updatedPlayers[playerIndex].isActive ?? true),
                isFinished: autoFinish ? true : (updatedPlayers[playerIndex].isFinished ?? false)
            };

            // Only advance turn if it was the current turn player (not a behind player)
            let nextTurnIndex = data.currentTurnIndex;
            if (isCurrentTurn) {
                // Find next active player (skip idle players if enough active remain)
                const { nextIndex, isIdlePlayer } = findNextTurnIndex(
                    updatedPlayers, data.currentTurnIndex, playerIndex
                );
                nextTurnIndex = nextIndex;

                // Give the next player a fresh timeout window
                if (nextTurnIndex !== playerIndex) {
                    updatedPlayers[nextTurnIndex] = {
                        ...updatedPlayers[nextTurnIndex],
                        lastActiveAt: Date.now()
                    };
                }
            }

            // Check if all players have finished (for auto-end)
            let activePlayers = updatedPlayers.filter(p => isPlayerActive(p));

            // Check if all remaining active players are idle - if so, auto-finish them
            const allRemainingAreIdle = activePlayers.length > 0 && activePlayers.every(p => p.isIdle);
            if (allRemainingAreIdle && flags.get('autoEndWhenAllIdle')) {
                // Auto-finish all idle players since no one is left to play
                for (let i = 0; i < updatedPlayers.length; i++) {
                    if (isPlayerActive(updatedPlayers[i]) && updatedPlayers[i].isIdle) {
                        updatedPlayers[i] = {
                            ...updatedPlayers[i],
                            isActive: false,
                            isFinished: true
                        };
                    }
                }
                activePlayers = updatedPlayers.filter(p => isPlayerActive(p));
            }

            const newStatus = activePlayers.length === 0 ? 'ended' : data.status;

            // Create move records - add return record if player was idle
            const moveRecords = [];
            if (wasIdle) {
                moveRecords.push({
                    playerId: this.playerId,
                    nickname: this.nickname,
                    timestamp: Date.now() - 1, // Slightly earlier so it appears before the play
                    type: 'return',
                    words: [],
                    score: 0
                });
            }
            moveRecords.push({
                moveId: moveId,
                playerId: this.playerId,
                nickname: this.nickname,
                timestamp: Date.now(),
                type: 'play',
                words: words.map(w => w.word),
                score: score,
                autoFinish: autoFinish
            });

            // Update game state
            transaction.update(this.gameRef, {
                boardTiles: newBoardTiles,
                tileBag: tileBag,
                players: updatedPlayers,
                currentTurnIndex: nextTurnIndex,
                isFirstMove: false,
                status: newStatus,
                moves: arrayUnion(...moveRecords)
            });
        });
    }

    createMoveId() {
        if (window.crypto && typeof window.crypto.randomUUID === 'function') {
            return window.crypto.randomUUID();
        }
        return `move_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    }

    async submitSwap() {
        if (!this.isMyTurn) {
            this.showMessage("It's not your turn!", 'error');
            this.localGame.exitSwapMode();
            return;
        }

        if (this.isSubmitting) {
            return;
        }

        const tilesToSwap = this.localGame.rack.filter(t =>
            this.localGame.tilesMarkedForSwap.has(t.id)
        );
        const swapCount = tilesToSwap.length;

        if (swapCount === 0) {
            this.localGame.exitSwapMode();
            return;
        }

        // Check if there are enough tiles in the bag
        if (this.gameState.tileBag.length < swapCount) {
            this.showMessage(`Only ${this.gameState.tileBag.length} tiles left in the bag!`, 'error');
            return;
        }

        this.isSubmitting = true;
        this.controlsEl.classList.add('disabled');
        this.showMessage('Swapping tiles...', 'info');

        try {
            await runTransaction(db, async (transaction) => {
                const docSnap = await transaction.get(this.gameRef);
                if (!docSnap.exists()) throw new Error('Game not found');

                const data = docSnap.data();
                const playerIndex = data.players.findIndex(p => p.id === this.playerId);

                if (playerIndex === -1) throw new Error('Player not in game');

                // Check if player can move using shared logic
                const moveStatus = canPlayerMove(data.players, data.currentTurnIndex, this.playerId);
                if (!moveStatus.canMove) {
                    throw new Error('NOT_YOUR_TURN');
                }

                const isCurrentTurn = moveStatus.reason === MOVE_REASON.TURN;
                const isCatchUpMove = moveStatus.reason === MOVE_REASON.BEHIND;

                // Get this player's data
                const player = data.players[playerIndex];

                // Verify tiles are still in rack
                const rackIds = new Set((player.rack || []).map(t => t.id));
                for (const tile of tilesToSwap) {
                    if (!rackIds.has(tile.id)) {
                        throw new Error('TILES_NOT_IN_RACK');
                    }
                }

                // Get tile bag and shuffle in the swapped tiles
                const tileBag = [...data.tileBag];

                // Draw new tiles first
                const newTiles = [];
                for (let i = 0; i < swapCount && tileBag.length > 0; i++) {
                    newTiles.push(tileBag.pop());
                }

                // Return swapped tiles to bag
                const swappedTileIds = new Set(tilesToSwap.map(t => t.id));
                tilesToSwap.forEach(t => {
                    tileBag.push({
                        letter: t.letter,
                        points: t.points,
                        isBlank: t.isBlank,
                        id: t.id
                    });
                });

                // Shuffle the bag
                for (let i = tileBag.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [tileBag[i], tileBag[j]] = [tileBag[j], tileBag[i]];
                }

                // Build new rack
                const newRack = player.rack.filter(t => !swappedTileIds.has(t.id));
                newRack.push(...newTiles);

                // Check if player was idle (for return message)
                const wasIdle = player.isIdle;

                // Update player data
                const updatedPlayers = [...data.players];
                const currentTurns = getTurnsTaken(updatedPlayers[playerIndex]);
                updatedPlayers[playerIndex] = {
                    ...updatedPlayers[playerIndex],
                    turnsTaken: currentTurns + 1,
                    rack: newRack,
                    lastActiveAt: Date.now(),
                    isIdle: false  // Clear idle status when player acts
                };

                // Only advance turn if it was the current turn player (not a behind player)
                let nextTurnIndex = data.currentTurnIndex;
                if (isCurrentTurn) {
                    // Find next active player (skip idle players if enough active remain)
                    const { nextIndex, isIdlePlayer } = findNextTurnIndex(
                        updatedPlayers, data.currentTurnIndex, playerIndex
                    );
                    nextTurnIndex = nextIndex;

                    // Give the next player a fresh timeout window
                    if (nextTurnIndex !== playerIndex) {
                        updatedPlayers[nextTurnIndex] = {
                            ...updatedPlayers[nextTurnIndex],
                            lastActiveAt: Date.now()
                        };
                    }
                }

                // Create move records - add return record if player was idle
                const moveRecords = [];
                if (wasIdle) {
                    moveRecords.push({
                        playerId: this.playerId,
                        nickname: this.nickname,
                        timestamp: Date.now() - 1,
                        type: 'return',
                        words: [],
                        score: 0
                    });
                }
                moveRecords.push({
                    moveId: this.createMoveId(),
                    playerId: this.playerId,
                    nickname: this.nickname,
                    timestamp: Date.now(),
                    type: 'swap',
                    swapCount: swapCount,
                    score: 0
                });

                // Update game state
                transaction.update(this.gameRef, {
                    tileBag: tileBag,
                    players: updatedPlayers,
                    currentTurnIndex: nextTurnIndex,
                    moves: arrayUnion(...moveRecords)
                });
            });

            this.localGame.exitSwapMode();
            this.showMessage(`Swapped ${swapCount} tile${swapCount > 1 ? 's' : ''}.`, 'success');
        } catch (error) {
            console.error('Failed to swap tiles:', error);
            if (error.message === 'NOT_YOUR_TURN') {
                this.showMessage('Turn already advanced. Please wait for your turn.', 'error');
            } else {
                this.showMessage('Failed to swap tiles. Please try again.', 'error');
            }
            this.localGame.exitSwapMode();
        } finally {
            this.isSubmitting = false;
        }
    }

    async snooze() {
        if (this.isSubmitting) {
            return; // Prevent double submission
        }

        this.isSubmitting = true;
        try {
            await runTransaction(db, async (transaction) => {
                const docSnap = await transaction.get(this.gameRef);
                if (!docSnap.exists()) throw new Error('Game not found');

                const data = docSnap.data();
                const playerIndex = data.players.findIndex(p => p.id === this.playerId);

                if (playerIndex === -1) throw new Error('Player not in game');

                // Check if this player is the current turn player
                const currentPlayer = data.players[data.currentTurnIndex];
                const isCurrentTurn = currentPlayer && currentPlayer.id === this.playerId && isPlayerActive(currentPlayer);

                const updatedPlayers = [...data.players];
                updatedPlayers[playerIndex] = {
                    ...updatedPlayers[playerIndex],
                    lastActiveAt: Date.now(),
                    isIdle: true  // Set idle status - player will be skipped until they play
                };

                // Only advance turn if it was the current turn player (not a behind player)
                let nextTurnIndex = data.currentTurnIndex;
                if (isCurrentTurn) {
                    // Find next active player (skip idle players if enough active remain)
                    const { nextIndex, isIdlePlayer } = findNextTurnIndex(
                        updatedPlayers, data.currentTurnIndex, playerIndex
                    );
                    nextTurnIndex = nextIndex;

                    // Give the next player a fresh timeout window
                    if (nextTurnIndex !== playerIndex) {
                        updatedPlayers[nextTurnIndex] = {
                            ...updatedPlayers[nextTurnIndex],
                            lastActiveAt: Date.now()
                        };
                    }
                }

                const moveRecord = {
                    playerId: this.playerId,
                    nickname: this.nickname,
                    timestamp: Date.now(),
                    type: 'snooze',
                    words: [],
                    score: 0
                };

                transaction.update(this.gameRef, {
                    players: updatedPlayers,
                    currentTurnIndex: nextTurnIndex,
                    moves: arrayUnion(moveRecord)
                });
            });

            this.showMessage('You went away - click anywhere to return', 'info');
        } catch (error) {
            console.error('Failed to snooze:', error);
            this.showMessage('Failed to go away. Please try again.', 'error');
        } finally {
            this.isSubmitting = false;
        }
    }

    async comeBackFromAway() {
        // Don't run if already submitting or not actually idle
        if (this.isSubmitting || !this.isCurrentPlayerIdle()) {
            return;
        }

        this.isSubmitting = true;
        try {
            await runTransaction(db, async (transaction) => {
                const docSnap = await transaction.get(this.gameRef);
                if (!docSnap.exists()) throw new Error('Game not found');

                const data = docSnap.data();
                const playerIndex = data.players.findIndex(p => p.id === this.playerId);

                if (playerIndex === -1) throw new Error('Player not in game');

                // Only proceed if player is actually idle
                if (!data.players[playerIndex].isIdle) {
                    return; // Already active, nothing to do
                }

                const updatedPlayers = [...data.players];
                updatedPlayers[playerIndex] = {
                    ...updatedPlayers[playerIndex],
                    lastActiveAt: Date.now(),
                    isIdle: false
                };

                const moveRecord = {
                    playerId: this.playerId,
                    nickname: this.nickname,
                    timestamp: Date.now(),
                    type: 'return',
                    words: [],
                    score: 0
                };

                transaction.update(this.gameRef, {
                    players: updatedPlayers,
                    moves: arrayUnion(moveRecord)
                });
            });

            this.showMessage("You're back! Play when ready.", 'info');
        } catch (error) {
            console.error('Failed to return from away:', error);
            // Silent fail - don't bother user with error for auto-return
        } finally {
            this.isSubmitting = false;
        }
    }

    async leave() {
        try {
            await runTransaction(db, async (transaction) => {
                const docSnap = await transaction.get(this.gameRef);
                if (!docSnap.exists()) return;

                const data = docSnap.data();
                const playerIndex = data.players.findIndex(p => p.id === this.playerId);

                if (playerIndex === -1) return;

                const updatedPlayers = [...data.players];
                updatedPlayers[playerIndex] = {
                    ...updatedPlayers[playerIndex],
                    isActive: false
                };

                // Check if this was the current player's turn
                let nextTurnIndex = data.currentTurnIndex;
                if (playerIndex === data.currentTurnIndex) {
                    // Find next active player (skip idle players if enough active remain)
                    const { nextIndex, isIdlePlayer } = findNextTurnIndex(
                        updatedPlayers, data.currentTurnIndex, playerIndex
                    );
                    nextTurnIndex = nextIndex;

                    // Give the next player a fresh timeout window
                    if (nextTurnIndex !== playerIndex) {
                        updatedPlayers[nextTurnIndex] = {
                            ...updatedPlayers[nextTurnIndex],
                            lastActiveAt: Date.now()
                        };
                    }
                }

                // Check if all players have left
                const activePlayers = updatedPlayers.filter(p => isPlayerActive(p));
                const newStatus = activePlayers.length === 0 ? 'ended' : data.status;

                transaction.update(this.gameRef, {
                    players: updatedPlayers,
                    currentTurnIndex: nextTurnIndex,
                    status: newStatus
                });
            });
        } catch (error) {
            console.error('Failed to leave game:', error);
        } finally {
            this.cleanup();
        }
    }

    async finishGame() {
        // Finish can be called at any time (not just on your turn)
        if (this.isSubmitting) {
            return;
        }

        // Return any pending tiles to rack first
        this.localGame.recallTiles();

        this.isSubmitting = true;
        this.controlsEl.classList.add('disabled');
        this.showMessage('Finishing game...', 'info');

        try {
            await runTransaction(db, async (transaction) => {
                const docSnap = await transaction.get(this.gameRef);
                if (!docSnap.exists()) throw new Error('Game not found');

                const data = docSnap.data();
                const playerIndex = data.players.findIndex(p => p.id === this.playerId);

                if (playerIndex === -1) throw new Error('Player not in game');

                // Check if player is still active (not already finished/left)
                const player = data.players[playerIndex];
                if (!isPlayerActive(player)) {
                    throw new Error('ALREADY_FINISHED');
                }

                const isCurrentTurn = data.currentTurnIndex === playerIndex;

                // Mark player as finished
                const updatedPlayers = [...data.players];
                updatedPlayers[playerIndex] = {
                    ...updatedPlayers[playerIndex],
                    isActive: false,
                    isFinished: true,
                    lastActiveAt: Date.now()
                };

                // Find next active player (skip idle players if enough active remain)
                let nextTurnIndex = data.currentTurnIndex;
                let foundActive = false;
                if (isCurrentTurn) {
                    const { nextIndex, isIdlePlayer } = findNextTurnIndex(
                        updatedPlayers, data.currentTurnIndex, playerIndex
                    );
                    if (nextIndex !== data.currentTurnIndex || isPlayerActive(updatedPlayers[nextIndex])) {
                        nextTurnIndex = nextIndex;
                        foundActive = true;
                    }

                    // Give the next player a fresh timeout window
                    if (foundActive && nextTurnIndex !== playerIndex) {
                        updatedPlayers[nextTurnIndex] = {
                            ...updatedPlayers[nextTurnIndex],
                            lastActiveAt: Date.now()
                        };
                    }
                }

                // Check if all remaining active players are idle - if so, auto-finish them
                const activePlayers = updatedPlayers.filter(p => isPlayerActive(p));
                const allRemainingAreIdle = activePlayers.length > 0 && activePlayers.every(p => p.isIdle);

                if (allRemainingAreIdle && flags.get('autoEndWhenAllIdle')) {
                    // Auto-finish all idle players since no one is left to play
                    for (let i = 0; i < updatedPlayers.length; i++) {
                        if (isPlayerActive(updatedPlayers[i]) && updatedPlayers[i].isIdle) {
                            updatedPlayers[i] = {
                                ...updatedPlayers[i],
                                isActive: false,
                                isFinished: true
                            };
                        }
                    }
                }

                const remainingActive = updatedPlayers.filter(p => isPlayerActive(p));
                const newStatus = remainingActive.length === 0 ? 'ended' : data.status;

                // Create move record
                const moveRecord = {
                    playerId: this.playerId,
                    nickname: this.nickname,
                    timestamp: Date.now(),
                    type: 'finish',
                    words: [],
                    score: 0
                };

                transaction.update(this.gameRef, {
                    players: updatedPlayers,
                    currentTurnIndex: foundActive ? nextTurnIndex : data.currentTurnIndex,
                    status: newStatus,
                    moves: arrayUnion(moveRecord)
                });
            });

            this.showMessage('You finished the game!', 'success');
        } catch (error) {
            console.error('Failed to finish game:', error);
            if (error.message === 'ALREADY_FINISHED') {
                this.showMessage('You have already finished.', 'error');
            } else {
                this.showMessage('Failed to finish. Please try again.', 'error');
            }
        } finally {
            this.isSubmitting = false;
        }
    }

    shouldShowSoftGameOver() {
        const myPlayer = this.gameState.players.find(p => p.id === this.playerId);
        if (!myPlayer || !myPlayer.isFinished) return false;
        if (this.gameState.status === 'ended') return false;
        const activePlayers = this.gameState.players.filter(p => isPlayerActive(p));
        return activePlayers.length > 0 && activePlayers.every(p => p.isIdle);
    }

    showSoftGameOver() {
        this.softGameOverActive = true;
        this.controlsEl.classList.add('disabled');
        this.showScoreboard(
            [...this.gameState.players].sort((a, b) => b.score - a.score),
            [...this.gameState.players].sort((a, b) => b.score - a.score)[0],
            { soft: true }
        );
        const overlay = document.getElementById('game-scoreboard');
        if (overlay) {
            this.cleanupAmbientFireworks = startAmbientFireworks(overlay, this._blimpMessages());
        }
        // Full celebration (fanfare + big fireworks) immediately
        this.cleanupCelebration = celebrate();
    }

    refreshSoftScoreboard() {
        this.showScoreboard(
            [...this.gameState.players].sort((a, b) => b.score - a.score),
            [...this.gameState.players].sort((a, b) => b.score - a.score)[0],
            { soft: true, animate: false }
        );
    }

    upgradeSoftToTrueGameOver() {
        // Keep ambient fireworks running — just refresh the scoreboard
        // to remove soft-game-over elements (waiting note, away badges)
        this.softGameOverActive = false;
        const sortedPlayers = [...this.gameState.players].sort((a, b) => b.score - a.score);
        const winner = sortedPlayers[0];
        this.showScoreboard(sortedPlayers, winner, { animate: false });
    }

    showGameOver() {
        // Sort players by score
        const sortedPlayers = [...this.gameState.players].sort((a, b) => b.score - a.score);
        const winner = sortedPlayers[0];

        // Create scoreboard overlay
        this.showScoreboard(sortedPlayers, winner);
        this.controlsEl.classList.add('disabled');

        // Fireworks + fanfare
        this.cleanupCelebration = celebrate();

        // Persistent ambient fireworks on the scoreboard background
        const overlay = document.getElementById('game-scoreboard');
        if (overlay) {
            this.cleanupAmbientFireworks = startAmbientFireworks(overlay, this._blimpMessages());
        }
    }

    _blimpMessages() {
        const moves = this.gameState.moves || [];
        const playMoves = moves.filter(m => m.type === 'play');
        const bestMove = playMoves.length > 0
            ? playMoves.reduce((best, m) => m.score > best.score ? m : best, playMoves[0])
            : null;

        const msgs = [];
        if (bestMove) {
            const word = bestMove.words?.[0]?.word;
            msgs.push(`Best play: ${word ? word.toUpperCase() + ' ' : ''}+${bestMove.score} by ${bestMove.nickname}`);
        }
        return msgs;
    }

    showScoreboard(sortedPlayers, winner, options = {}) {
        const { soft = false, animate = true } = options;

        // Reuse existing overlay if present (preserves ambient fireworks canvas)
        let overlay = document.getElementById('game-scoreboard');
        if (overlay) {
            // Remove only the modal, not the canvas
            const existingModal = overlay.querySelector('.scoreboard-modal');
            if (existingModal) existingModal.remove();
        } else {
            overlay = document.createElement('div');
            overlay.id = 'game-scoreboard';
            overlay.className = 'scoreboard-overlay';
        }

        const medals = ['🥇', '🥈', '🥉'];

        const modal = document.createElement('div');
        modal.className = 'scoreboard-modal';
        if (!animate) {
            modal.classList.add('no-animate');
        }

        // Title
        const title = document.createElement('h2');
        title.className = 'scoreboard-title';
        title.textContent = 'Game Over!';
        modal.appendChild(title);

        // Winner announcement
        const winnerEl = document.createElement('div');
        winnerEl.className = 'scoreboard-winner';
        winnerEl.textContent = `🏆 ${winner.nickname} wins!`;
        modal.appendChild(winnerEl);

        // Player list
        const list = document.createElement('ul');
        list.className = 'scoreboard-list';

        sortedPlayers.forEach((player, index) => {
            const li = document.createElement('li');
            li.className = 'scoreboard-player';
            if (player.id === this.playerId) {
                li.classList.add('you');
            }

            // Medal for top 3
            if (index < 3) {
                const medal = document.createElement('span');
                medal.className = 'scoreboard-medal';
                medal.textContent = medals[index];
                li.appendChild(medal);
            }

            // Show rank number for players outside top 3 (medals cover top 3)
            if (index >= 3) {
                const rank = document.createElement('span');
                rank.className = 'scoreboard-rank';
                rank.textContent = `${index + 1}.`;
                li.appendChild(rank);
            }

            const name = document.createElement('span');
            name.className = 'scoreboard-name';
            name.textContent = player.nickname;

            // Away badge for idle players in soft game over
            if (soft && isPlayerActive(player) && player.isIdle) {
                const awayBadge = document.createElement('span');
                awayBadge.className = 'scoreboard-away';
                awayBadge.textContent = 'Away';
                name.appendChild(awayBadge);
            }

            const score = document.createElement('span');
            score.className = 'scoreboard-score';
            score.textContent = player.score === 0 ? '🥚' : player.score;

            const avg = document.createElement('span');
            avg.className = 'scoreboard-avg';
            const wordsPlayed = player.wordsPlayed || 0;
            const avgScore = wordsPlayed > 0 ? (player.score / wordsPlayed).toFixed(1) : '0';
            avg.textContent = `(${avgScore}/play)`;

            li.appendChild(name);
            li.appendChild(score);
            li.appendChild(avg);
            list.appendChild(li);
        });

        modal.appendChild(list);

        // Waiting note for soft game over
        if (soft) {
            const waitingNote = document.createElement('div');
            waitingNote.className = 'scoreboard-waiting-note';
            waitingNote.textContent = 'Away players can still return and play';
            modal.appendChild(waitingNote);
        }

        // Game stats
        const moves = this.gameState.moves || [];
        const playMoves = moves.filter(m => m.type === 'play');
        const totalWords = playMoves.reduce((sum, m) => sum + (m.words?.length || 0), 0);
        const bestMove = playMoves.length > 0
            ? playMoves.reduce((best, m) => m.score > best.score ? m : best, playMoves[0])
            : null;

        if (bestMove) {
            const statsRow = document.createElement('div');
            statsRow.className = 'scoreboard-stats';

            const totalStat = document.createElement('div');
            totalStat.className = 'scoreboard-stat';
            const totalVal = document.createElement('span');
            totalVal.className = 'scoreboard-stat-value';
            totalVal.textContent = totalWords;
            const totalLabel = document.createElement('span');
            totalLabel.className = 'scoreboard-stat-label';
            totalLabel.textContent = 'Words';
            totalStat.appendChild(totalVal);
            totalStat.appendChild(totalLabel);

            const bestStat = document.createElement('div');
            bestStat.className = 'scoreboard-stat';
            const bestVal = document.createElement('span');
            bestVal.className = 'scoreboard-stat-value';
            bestVal.textContent = `+${bestMove.score}`;
            const bestLabel = document.createElement('span');
            bestLabel.className = 'scoreboard-stat-label';
            bestLabel.textContent = 'Best Play';
            bestStat.appendChild(bestVal);
            bestStat.appendChild(bestLabel);
            bestStat.title = bestMove.nickname;

            const turnsStat = document.createElement('div');
            turnsStat.className = 'scoreboard-stat';
            const turnsVal = document.createElement('span');
            turnsVal.className = 'scoreboard-stat-value';
            turnsVal.textContent = playMoves.length;
            const turnsLabel = document.createElement('span');
            turnsLabel.className = 'scoreboard-stat-label';
            turnsLabel.textContent = 'Turns';
            turnsStat.appendChild(turnsVal);
            turnsStat.appendChild(turnsLabel);

            statsRow.appendChild(totalStat);
            statsRow.appendChild(bestStat);
            statsRow.appendChild(turnsStat);
            modal.appendChild(statsRow);
        }

        // Back to menu button
        const backBtn = document.createElement('button');
        backBtn.className = 'scoreboard-btn';
        backBtn.textContent = 'Back to Menu';
        backBtn.addEventListener('click', () => {
            overlay.remove();
            if (this.cleanupAmbientFireworks) {
                this.cleanupAmbientFireworks();
                this.cleanupAmbientFireworks = null;
            }
            if (this.cleanupCelebration) this.cleanupCelebration();
            this.softGameOverActive = false;
            this.cleanup();
            window.app.showScreen('menu');
        });
        modal.appendChild(backBtn);

        overlay.appendChild(modal);
        if (!overlay.parentNode) {
            document.body.appendChild(overlay);
        }
    }

    showMessage(text, type) {
        const MIN_DISPLAY_MS = 3000; // Minimum 3 seconds display time
        const now = Date.now();

        // Don't let info messages overwrite error messages too quickly
        // But success messages can always override (they indicate the error was resolved)
        if (type === 'info') {
            if (this.errorMessageUntil && now < this.errorMessageUntil) {
                return; // Don't overwrite recent error
            }
        }

        // Check if current message needs more display time
        if (type !== 'error' && this.messageShownAt && now < this.messageShownAt + MIN_DISPLAY_MS) {
            // Cancel any existing pending message
            if (this.pendingMessageTimer) {
                clearTimeout(this.pendingMessageTimer);
            }
            // Schedule this message for later
            const delay = this.messageShownAt + MIN_DISPLAY_MS - now;
            this.pendingMessageTimer = setTimeout(() => {
                this.pendingMessageTimer = null;
                this.showMessage(text, type);
            }, delay);
            return;
        }

        // Clear any pending message since we're showing a new one now
        if (this.pendingMessageTimer) {
            clearTimeout(this.pendingMessageTimer);
            this.pendingMessageTimer = null;
        }

        this.messageEl.textContent = text;
        this.messageEl.className = 'message ' + type;
        this.messageShownAt = now;

        // Error messages get protection from being overwritten by info messages
        if (type === 'error') {
            this.errorMessageUntil = now + 5000; // 5 seconds
        } else if (type === 'success') {
            this.errorMessageUntil = null; // Clear error protection on success
        }
    }

    cleanup() {
        // Disconnect flag sync
        flags.onFlagChanged = null;

        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
        if (this.timeoutCheckInterval) {
            clearInterval(this.timeoutCheckInterval);
            this.timeoutCheckInterval = null;
        }
        // Clear pending message timer
        if (this.pendingMessageTimer) {
            clearTimeout(this.pendingMessageTimer);
            this.pendingMessageTimer = null;
        }
        // Clean up ambient fireworks
        if (this.cleanupAmbientFireworks) {
            this.cleanupAmbientFireworks();
            this.cleanupAmbientFireworks = null;
        }
        this.softGameOverActive = false;
        // Remove activity listeners
        if (this.activityHandler && this.activityContainer) {
            this.activityContainer.removeEventListener('click', this.activityHandler);
            this.activityContainer.removeEventListener('keydown', this.activityHandler);
            this.activityHandler = null;
            this.activityContainer = null;
        }
        if (this.localGame) {
            this.localGame.destroy();
            this.localGame = null;
        }
        // Restore original tab title
        document.title = this.originalTitle;
    }
}
